import {useEffect, useMemo} from 'react';
import {useTranslation} from 'react-i18next';
import {NodeType} from 'crqlar-react-common/dist/models/automation/automation';
import {
    continueIfError, continueIfFields, readContinueIfGroups, writeContinueIfGroups
} from '../../../config/continueIf';
import NodeEditorLayout from '../NodeEditorLayout';
import {NodeEditorV2Props} from '../types';
import IfConditionRules from './IfConditionRules';

const ContinueIfEditorV2 = ({
    nodeConfig, setNodeConfig, upstreamNodes = [], flowNodes = [], registerBeforeSave, readOnly
}: NodeEditorV2Props) => {
    const {t} = useTranslation();
    const channels = useMemo(() => {
        const types = new Set([...flowNodes, ...upstreamNodes].map(node => node.type));
        return [
            ...(types.has(NodeType.SEND_EMAIL) ? ['EMAIL'] : []),
            ...(types.has(NodeType.SEND_PUSH) ? ['PUSH'] : [])
        ];
    }, [flowNodes, upstreamNodes]);
    const groups = useMemo(() => readContinueIfGroups(nodeConfig, channels), [nodeConfig, channels]);
    const fields = useMemo(() => continueIfFields(upstreamNodes, t, channels), [upstreamNodes, t, channels]);
    const normalized = useMemo(() => writeContinueIfGroups(nodeConfig, groups), [nodeConfig, groups]);
    const error = continueIfError(normalized, fields);

    useEffect(() => {
        registerBeforeSave?.(async () => error ? false : {config: normalized});
        return () => registerBeforeSave?.(null);
    }, [registerBeforeSave, error, normalized]);

    return (
        <NodeEditorLayout state={error ? 'wontRun' : 'configured'}
                          summary={t(error || 'Continues when the conditions match. Otherwise, this path ends.')}>
            <IfConditionRules
                groups={groups}
                fieldOptions={fields}
                setGroups={next => setNodeConfig(writeContinueIfGroups(nodeConfig, next))}
                readOnly={readOnly}
                emptyText={t('Add a condition to decide who continues.')}
            />
        </NodeEditorLayout>
    );
};

export default ContinueIfEditorV2;
